// src/pages/WhyChooseUsPage.js
import React from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  useColorModeValue,
  Button,
  Icon,
  Divider,
  Container,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

// Importing the same icons from the homepage component
import { GoGear, GoPeople, GoTrophy } from 'react-icons/go';
import { BiTimeFive } from 'react-icons/bi';

// A new, reusable component for the imageless feature sections
const FeatureSection = ({ icon, title, text, bg }) => {
  return (
    <Box w="full" py={{ base: 16, md: 20 }} bg={bg}>
      <Container maxW="3xl">
        <VStack spacing={6} textAlign="center">
          <Icon as={icon} boxSize={16} color="orange.500" />
          <Heading as="h2" size="xl" fontWeight="bold">
            {title}
          </Heading>
          <Text fontSize="xl" color={useColorModeValue('gray.600', 'gray.300')} lineHeight="tall">
            {text}
          </Text>
        </VStack>
      </Container>
    </Box>
  );
};

export default function WhyChooseUsPage() {
  const bgBase = useColorModeValue('white', 'gray.800');
  const bgAlternate = useColorModeValue('gray.50', 'gray.700');

  return (
    <>
      <Header />
      <Box as="main">
        {/* Page Header */}
        <Box bg={bgBase}>
          <VStack maxW="7xl" mx="auto" px={8} spacing={4} py={{ base: 16, md: 24 }} textAlign="center">
            <Breadcrumb>
              <BreadcrumbItem>
                <BreadcrumbLink as={RouterLink} to="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem isCurrentPage>
                <BreadcrumbLink href="#">Why Choose Us</BreadcrumbLink>
              </BreadcrumbItem>
            </Breadcrumb>
            <Heading as="h1" size="2xl" fontWeight="extrabold">
              Building Trust, Delivering Excellence
            </Heading>
            <Text maxW="3xl" color="gray.600" fontSize="lg">
              Our reputation is built on a foundation of integrity, expertise, and an unwavering commitment to our clients' success. Discover why we are the trusted partner for your most important projects.
            </Text>
          </VStack>
        </Box>

        {/* --- REDESIGNED FEATURES SECTION --- */}
        <VStack spacing={0} align="stretch">
          <FeatureSection
            icon={GoGear}
            title="Advanced Technology"
            text="We harness the power of the latest construction technologies, including Building Information Modeling (BIM), drone surveying, and advanced project management software. This allows us to improve accuracy, optimize schedules, and ensure cost-efficiency from groundbreaking to completion, giving you a smarter, more transparent building experience."
            bg={bgAlternate}
          />
          <FeatureSection
            icon={GoPeople}
            title="Expert Team"
            text="Our people are our greatest asset. Our team consists of industry-certified project managers, engineers, and skilled craftspeople who bring decades of collective experience to the table. We foster a culture of continuous learning and collaboration, ensuring we can tackle any challenge with innovative and effective solutions."
            bg={bgBase}
          />
          <FeatureSection
            icon={BiTimeFive}
            title="On-Time Delivery"
            text="We understand that time is a critical resource. Our meticulous planning, proactive communication, and efficient site management protocols are designed to keep projects on schedule. We have a proven track record of meeting deadlines without compromising on the quality or safety of our work, giving you peace of mind."
            bg={bgAlternate}
          />
          <FeatureSection
            icon={GoTrophy}
            title="Award Winning"
            text="Our commitment to excellence has been consistently recognized by the industry. We are proud recipients of multiple awards for construction quality, project management, and, most importantly, our outstanding safety record. These accolades reflect our dedication to being a leader in all aspects of our work."
            bg={bgBase}
          />
        </VStack>

        {/* Call to Action */}
        <Box bg={bgAlternate}>
            <VStack spacing={6} textAlign="center" py={20}>
                <Heading as="h2" size="xl">Ready to Build with Confidence?</Heading>
                <Text maxW="2xl" color="gray.600">
                Partner with a team that is as invested in your project's success as you are.
                </Text>
                <Button as={RouterLink} to="/contact" colorScheme="orange" size="lg">
                Start a Conversation
                </Button>
            </VStack>
        </Box>
      </Box>
      <Footer />
    </>
  );
}